/**
 * Il piano proposto — «cosa, quanto, quando, su quale macchina, in che ordine».
 *
 * Sostituisce la vista del piano MES nella sezione Piano: da qui in avanti il
 * modulo non riordina un piano che riceve, ne costruisce uno dal fabbisogno.
 *
 * ## Quattro cose, e l'ordine non è casuale
 *
 * 1. **Come non ce la fa.** Righe in ritardo e violazioni stanno in cima, non in
 *    fondo: «una proposta infattibile presentata come fattibile è il peggior
 *    esito possibile» (SPEC §4.3). Se il piano non regge, chi lo apre lo deve
 *    sapere prima di leggerlo.
 * 2. **Perché questo piano e non un altro.** Il criterio scelto, il conto che
 *    l'ha scelto, gli altri candidati col loro punteggio, e cosa dovrebbe
 *    contare di più perché la scelta cambiasse.
 * 3. **Quanto sono cariche le macchine**, giorno per giorno.
 * 4. **Le lavorazioni**, con il motivo di ognuna.
 *
 * ## Cosa non fa
 *
 * Non approva, non esporta, non scrive niente da nessuna parte. Il pulsante
 * «Rigenera» ricostruisce la proposta; non esiste un pulsante che la applichi,
 * perché non c'è niente su cui applicarla — e un pulsante che promette di
 * applicare qualcosa senza farlo è il difetto che questo modulo aveva già (il
 * vecchio «Applica al piano», che non applicava nulla).
 */
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Download,
  Info,
  Loader2,
  RefreshCw,
  Scale,
} from "lucide-react";
import { useState } from "react";

import { RigaTesta } from "@/components/RigaTesta";
import { useM2GeneraPiano, useM2PianoProposto, useM2StatoExport } from "@/hooks/useM2";
import { cn } from "@/lib/utils";
import { fmtData, fmtMinuti, num } from "@/pages/M2/format";
import { SetupTag, StampoChip } from "@/pages/M2/shared";
import type {
  CaricoLinea,
  PianoPropostoRigaRead,
  PianoPropostoTestata,
} from "@/types/m2";

const NOME_METRICA: Record<string, string> = {
  puntualita: "la puntualità",
  cambi_stampo: "i cambi di trafila",
  anticipo: "il magazzino (anticipo)",
  saturazione: "la saturazione delle macchine",
};

type Fase = "" | "estrusione" | "confezionamento";

export function PianoPropostoM2({ onVaiAlFabbisogno }: { onVaiAlFabbisogno: () => void }) {
  const [fase, setFase] = useState<Fase>("");
  const [linea, setLinea] = useState<string>("");
  const [soloRitardo, setSoloRitardo] = useState(false);
  const { data, isLoading } = useM2PianoProposto({
    fase: fase || undefined,
    linea: linea || undefined,
  });
  const genera = useM2GeneraPiano();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Caricamento…
      </div>
    );
  }

  if (!data?.piano) {
    return (
      <NessunPiano
        onGenera={() => genera.mutate(undefined)}
        inCorso={genera.isPending}
        errore={genera.error instanceof Error ? genera.error.message : null}
        onVaiAlFabbisogno={onVaiAlFabbisogno}
      />
    );
  }

  const p = data.piano;
  const elenco = soloRitardo ? data.in_ritardo : data.righe;

  return (
    <div className="space-y-4">
      <RigaTesta
        contesto={`${num(p.n_righe)} lavorazioni · ${fmtData(p.orizzonte_da)} → ${fmtData(p.orizzonte_a)}`}
        caricatoIl={p.fabbisogno_caricato_il}
        etichettaData="dal fabbisogno del"
        titoloBadge="Il piano è costruito sul fabbisogno caricato: più invecchia, meno descrive le commesse di adesso"
      >
        <button
          type="button"
          onClick={() => genera.mutate(undefined)}
          disabled={genera.isPending}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-primary hover:opacity-80 disabled:opacity-50"
        >
          {genera.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
          Rigenera la proposta
        </button>
      </RigaTesta>

      <DoveNonCeLaFa piano={p} />
      <PercheQuesto piano={p} />
      <Avvertenze piano={p} />
      <Carico carico={data.carico} attiva={linea} onScegli={setLinea} />

      {/* ── le lavorazioni ── */}
      <section>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Le lavorazioni
          </h2>
          <div className="ml-auto flex flex-wrap gap-2">
            {(
              [
                ["", "Tutte"],
                ["estrusione", "Estrusione"],
                ["confezionamento", "Confezionamento"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setFase(k)}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-[11.5px] font-medium",
                  fase === k ? "border-primary bg-accent" : "bg-card hover:bg-accent/40"
                )}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSoloRitardo((v) => !v)}
              className={cn(
                "rounded-md border px-2.5 py-1 text-[11.5px] font-medium",
                soloRitardo
                  ? "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                  : "bg-card hover:bg-accent/40"
              )}
            >
              Solo in ritardo ({p.n_in_ritardo})
            </button>
            {linea && (
              <button
                type="button"
                onClick={() => setLinea("")}
                className="rounded-md border bg-card px-2.5 py-1 text-[11.5px] font-medium hover:bg-accent/40"
              >
                Linea {linea} ✕
              </button>
            )}
          </div>
        </div>
        {elenco && <Lavorazioni elenco={elenco} />}
      </section>

      <PostoExport />
    </div>
  );
}

/**
 * Dove uscirà il piano, quando ci sarà un formato concordato (§4.10).
 *
 * Esiste ed è visibile apposta: «il posto da cui uscirà il piano è previsto e
 * visibile, in stato non ancora disponibile». Un posto previsto e dichiarato
 * non pronto è un'informazione; un posto assente è una domanda che torna a ogni
 * riunione.
 */
function PostoExport() {
  const { data } = useM2StatoExport();
  if (!data) return null;
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-dashed bg-muted/30 px-3.5 py-3">
      <Download className="size-4 shrink-0 text-muted-foreground" />
      <span className="text-[12.5px] font-medium">{data.titolo}</span>
      <span
        className="rounded-full border px-2 py-0.5 text-[10.5px] text-muted-foreground"
        aria-disabled
      >
        non ancora disponibile
      </span>
      <span className="w-full text-[11.5px] text-muted-foreground">{data.motivo}</span>
    </section>
  );
}

// ── 1. dove non ce la fa ─────────────────────────────────────────────

function DoveNonCeLaFa({ piano }: { piano: PianoPropostoTestata }) {
  const ok = piano.n_in_ritardo === 0 && piano.n_violazioni === 0;
  if (ok) {
    return (
      <div className="flex gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-[12.5px] text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
        <CheckCircle2 className="size-4 shrink-0" />
        <div>
          <b>La proposta sta in piedi:</b> nessuna consegna oltre la data richiesta, nessuna
          regola di sequenza violata, nessuna macchina oltre capacità.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3.5 dark:border-amber-900 dark:bg-amber-950/40">
      <div className="flex gap-2.5 text-[12.5px]">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div>
          <div className="font-semibold text-amber-900 dark:text-amber-200">
            Dove questa proposta non ce la fa
          </div>
          <div className="mt-0.5 text-amber-900/90 dark:text-amber-200/90">
            {piano.n_in_ritardo > 0 && (
              <>
                {/* ⚠️ Niente somma dei giorni: ritardi paralleli sommati non
                    fanno una durata, e il numero cresce da solo ogni giorno. Il
                    caso peggiore invece si legge. */}
                <b>{num(piano.n_in_ritardo)} lavorazioni</b> finiscono dopo la data richiesta, la
                più in ritardo di <b>{num(piano.giorni_ritardo_max)} giorni</b>.{" "}
              </>
            )}
            {piano.n_violazioni > 0 && (
              <>
                <b>{num(piano.n_violazioni)} regole di sequenza</b> sono state violate: sono
                morbide, quindi la proposta le può violare, ma lo dichiara.
              </>
            )}
          </div>
        </div>
      </div>
      {piano.violazioni.length > 0 && (
        <div className="space-y-1 pl-7 text-[11.5px] text-amber-900/90 dark:text-amber-200/90">
          {piano.violazioni.map((v) => (
            <div key={v.regola}>
              «{v.regola}» — {v.n} volte, {fmtMinuti(v.minuti)}. {v.esempio}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 2. perché questo piano ───────────────────────────────────────────

function PercheQuesto({ piano }: { piano: PianoPropostoTestata }) {
  const [aperto, setAperto] = useState(false);
  const s = piano.punteggio;
  return (
    <section className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-3.5 py-3">
        <Scale className="size-4 shrink-0 text-primary" />
        <div className="text-[12.5px]">
          Criterio <b>{piano.preset_nome.replace(/_/g, " ")}</b> → {s.criterio}.{" "}
          <span className="text-muted-foreground">
            Non si produce più di <b>{s.tetto_anticipo_giorni} giorni</b> prima della consegna.
          </span>
        </div>
        <button
          type="button"
          onClick={() => setAperto((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:opacity-80"
        >
          Perché questo e non un altro
          <ChevronDown className={cn("size-3.5 transition-transform", aperto && "rotate-180")} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 p-3 lg:grid-cols-4">
        <Numero n={s.cambi_stampo} label="Cambi di trafila" sotto={fmtMinuti(s.minuti_trafila)} />
        <Numero
          n={piano.n_in_ritardo}
          label="Lavorazioni in ritardo di produzione"
          sotto={
            piano.n_in_ritardo_a_chiamata > 0
              ? `la più in ritardo di ${piano.giorni_ritardo_max} giorni · ${piano.n_in_ritardo_a_chiamata} a chiamata, contate a parte`
              : `la più in ritardo di ${piano.giorni_ritardo_max} giorni`
          }
        />
        <Numero
          n={s.giorni_anticipo}
          label="Giorni di anticipo"
          sotto="merce prodotta prima del bisogno"
        />
        <Numero
          n={s.giornate_macchina}
          label="Giornate-macchina"
          sotto={`+ ${fmtMinuti(s.minuti_ricetta)} di cambi ricetta`}
        />
      </div>

      {aperto && (
        <div className="space-y-3 border-t p-3.5">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-1.5 text-left font-medium">Strategia</th>
                  <th className="px-2 py-1.5 text-right font-medium">Punteggio</th>
                  <th
                    className="px-2 py-1.5 text-right font-medium"
                    title="Somma dei giorni di ritardo di tutte le lavorazioni: serve a confrontare le strategie fra loro, non è una durata"
                  >
                    Ritardo (indice)
                  </th>
                  <th
                    className="px-2 py-1.5 text-right font-medium"
                    title="Somma dei giorni di anticipo: come sopra, è un indice di confronto"
                  >
                    Anticipo (indice)
                  </th>
                  <th className="px-2 py-1.5 text-right font-medium">Trafila</th>
                  <th className="px-2 py-1.5 text-right font-medium">Giornate</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {piano.alternative.map((a) => {
                  const scelta = a.strategia === s.strategia;
                  return (
                    <tr key={a.strategia} className={cn(scelta && "bg-accent/60 font-medium")}>
                      <td className="px-2 py-1.5">
                        {a.criterio}
                        {scelta && (
                          <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[9.5px] text-primary-foreground">
                            scelta
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{a.costo.toFixed(1)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {num(a.giorni_ritardo)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {num(a.giorni_anticipo)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{a.cambi_stampo}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{a.giornate_macchina}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Il pezzo che risponde a «e se per me contasse un'altra cosa?» — e,
              quando la risposta è «niente cambierebbe», lo dice invece di
              lasciar pensare che la manopola sia rotta. */}
          <div className="rounded-lg bg-muted/50 p-3 text-[12px]">
            <div className="font-medium">Se contasse di più un'altra cosa</div>
            <ul className="mt-1 space-y-0.5 text-muted-foreground">
              {s.sensibilita.map((x) => (
                <li key={x.metrica}>
                  dando tutto il peso a <b>{NOME_METRICA[x.metrica] ?? x.metrica}</b>:{" "}
                  {x.cambierebbe ? (
                    <span className="text-foreground">{x.criterio}</span>
                  ) : (
                    <>la proposta non cambierebbe</>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              Pesi attuali —{" "}
              {Object.entries(s.pesi)
                .map(([k, v]) => `${NOME_METRICA[k] ?? k}: ${v}`)
                .join(" · ")}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Numero({ n, label, sotto }: { n: number; label: string; sotto?: string }) {
  return (
    <div className="rounded-lg border bg-background p-2.5">
      <div className="text-xl font-semibold leading-tight tabular-nums">{num(n)}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      {sotto && <div className="text-[10.5px] text-muted-foreground/80">{sotto}</div>}
    </div>
  );
}

// ── 3. avvertenze e ipotesi ──────────────────────────────────────────

function Avvertenze({ piano }: { piano: PianoPropostoTestata }) {
  const [aperto, setAperto] = useState(false);
  if (piano.avvertenze.length === 0) return null;
  // Le **ipotesi** stanno sempre in vista: sono la cornice entro cui il piano va
  // letto (che si produce anche di domenica, che si pianifica a giornate), e
  // nasconderle dietro un «mostra tutte» le rende, di fatto, non dichiarate.
  // Trovato guardando la pagina: erano finite sotto il taglio.
  const ipotesi = piano.avvertenze.filter((a) => a.ipotesi);
  const daGuardare = piano.avvertenze.filter((a) => !a.ipotesi);
  const visibili = aperto ? daGuardare : daGuardare.slice(0, 3);
  return (
    <section className="rounded-lg border bg-muted/40 p-3">
      <div className="flex items-center gap-2">
        <Info className="size-4 shrink-0 text-primary" />
        <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Su cosa poggia questa proposta
        </h2>
        {daGuardare.length > 3 && (
          <button
            type="button"
            onClick={() => setAperto((v) => !v)}
            className="ml-auto text-[12px] font-medium text-primary hover:opacity-80"
          >
            {aperto ? "Mostra meno" : `Tutte e ${daGuardare.length}`}
          </button>
        )}
      </div>
      <div className="mt-2 space-y-1.5">
        {visibili.map((a) => (
          <div key={a.codice} className="text-[12px]">
            <span className="font-medium">{a.titolo}</span>{" "}
            <span className="text-muted-foreground">{a.dettaglio}</span>
          </div>
        ))}
      </div>
      {ipotesi.length > 0 && (
        <div className="mt-2.5 space-y-1 border-t pt-2.5">
          {ipotesi.map((a) => (
            <div key={a.codice} className="text-[11.5px] text-muted-foreground">
              <b className="font-medium">{a.titolo}.</b> {a.dettaglio}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── 4. il carico delle macchine ──────────────────────────────────────

function Carico({
  carico,
  attiva,
  onScegli,
}: {
  carico: CaricoLinea[];
  attiva: string;
  onScegli: (c: string) => void;
}) {
  if (carico.length === 0) return null;
  return (
    <section>
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Quanto sono cariche le macchine
      </h2>
      <div className="space-y-1.5 rounded-lg border bg-card p-3">
        {carico.map((c) => {
          const max = Math.max(...c.giorni.map((g) => g.saturazione_pct ?? 0), 100);
          return (
            <button
              key={c.codice_macchina}
              type="button"
              onClick={() => onScegli(attiva === c.codice_macchina ? "" : c.codice_macchina)}
              className={cn(
                "flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-accent/40",
                attiva === c.codice_macchina && "bg-accent/60"
              )}
            >
              <span className="w-40 shrink-0 truncate text-[12px]">
                {c.nome ?? c.codice_macchina}
                <span className="ml-1 text-[10.5px] text-muted-foreground">
                  {c.tipo === "estrusione" ? "estr." : "conf."}
                </span>
              </span>
              {/* Una barretta per giorno: si vede a colpo d'occhio dove il piano
                  preme e dove la macchina è ferma. */}
              <span className="flex min-w-0 flex-1 items-end gap-px">
                {c.giorni.map((g) => (
                  <span
                    key={g.giorno}
                    title={`${fmtData(g.giorno)} · ${num(g.quantita)} ${c.um ?? ""} · ${g.saturazione_pct ?? "?"}%`}
                    className={cn(
                      "w-1.5 shrink-0 rounded-sm",
                      g.oltre_capacita
                        ? "bg-red-600"
                        : (g.saturazione_pct ?? 0) > 80
                          ? "bg-brand-600"
                          : "bg-brand-300 dark:bg-brand-800"
                    )}
                    style={{ height: `${Math.max(3, ((g.saturazione_pct ?? 0) / max) * 22)}px` }}
                  />
                ))}
              </span>
              <span className="w-40 shrink-0 text-right text-[11px] text-muted-foreground">
                {c.giorni_impegnati} giornate
                {c.giorni_oltre_capacita > 0 && (
                  <b className="text-red-700 dark:text-red-400">
                    {" "}
                    · {c.giorni_oltre_capacita} oltre capacità
                  </b>
                )}
                {!c.capacita_confermata && <span className="opacity-70"> · capacità dedotta</span>}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── 5. le lavorazioni ────────────────────────────────────────────────

function Lavorazioni({
  elenco,
}: {
  elenco: { totale: number; mostrati: number; items: PianoPropostoRigaRead[] };
}) {
  if (elenco.totale === 0) {
    return (
      <p className="rounded-lg border border-dashed bg-muted p-6 text-center text-sm text-muted-foreground">
        Nessuna lavorazione con questi filtri.
      </p>
    );
  }
  // Raggruppate per giornata: è così che si guarda un piano di produzione.
  const perGiorno = new Map<string, PianoPropostoRigaRead[]>();
  for (const r of elenco.items) {
    const k = r.giorno ?? "—";
    if (!perGiorno.has(k)) perGiorno.set(k, []);
    perGiorno.get(k)!.push(r);
  }

  return (
    <div className="space-y-3">
      {elenco.mostrati < elenco.totale && (
        <p className="text-[11.5px] text-amber-700 dark:text-amber-400">
          Ne vedi {num(elenco.mostrati)} di {num(elenco.totale)}.
        </p>
      )}
      {[...perGiorno.entries()].map(([giorno, righe], i) => (
        <Giornata key={giorno} giorno={giorno} righe={righe} apertaDiDefault={i < GIORNI_APERTI} />
      ))}
    </div>
  );
}

/** Quante giornate restano aperte all'apertura della pagina. Le altre si
 *  espandono con un clic: 252 lavorazioni tutte aperte facevano una pagina alta
 *  diciannovemila pixel, e §4.8 chiede una vista **navigabile** per giornata —
 *  «passare dalla proposta al dettaglio e viceversa senza perdere il filo». */
const GIORNI_APERTI = 2;

function Giornata({
  giorno,
  righe,
  apertaDiDefault,
}: {
  giorno: string;
  righe: PianoPropostoRigaRead[];
  apertaDiDefault: boolean;
}) {
  const [aperta, setAperta] = useState(apertaDiDefault);
  const macchine = [...new Set(righe.map((r) => r.linea_codice_macchina))].filter(Boolean);
  const inRitardo = righe.filter((r) => r.giorni_ritardo > 0).length;
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <button
        type="button"
        onClick={() => setAperta((v) => !v)}
        className="flex w-full items-center gap-2 border-b bg-muted/50 px-3.5 py-2 text-left text-[12px] font-medium hover:bg-accent/50"
      >
        <ChevronDown className={cn("size-3.5 transition-transform", !aperta && "-rotate-90")} />
        <CalendarClock className="size-3.5 text-muted-foreground" />
        {fmtData(giorno)}
        <span className="font-normal text-muted-foreground">
          · {righe.length} lavorazion{righe.length === 1 ? "e" : "i"} su {macchine.length}{" "}
          macchin{macchine.length === 1 ? "a" : "e"}
          {macchine.length > 0 && ` (${macchine.join(", ")})`}
        </span>
        {inRitardo > 0 && (
          <span className="ml-auto rounded bg-red-100 px-1.5 py-0.5 text-[10.5px] font-medium text-red-800 dark:bg-red-950 dark:text-red-300">
            {inRitardo} in ritardo
          </span>
        )}
      </button>
      {aperta && (
        <>
          {righe.map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-3.5 py-2 text-[12.5px] first:border-t-0"
            >
              <span className="w-16 shrink-0 text-[11px] text-muted-foreground">
                {r.linea_codice_macchina}
              </span>
              <StampoChip stampo={r.stampo_code} />
              <span className="min-w-0 shrink-0 font-mono text-[11.5px]">{r.codice}</span>
              <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                {r.descrizione}
              </span>
              <span className="shrink-0 tabular-nums">
                {num(r.quantita ?? 0)} {r.um}
              </span>
              {r.durata_min ? (
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {fmtMinuti(r.durata_min)}
                </span>
              ) : null}
              {/* Il setup come etichetta, non come frase: il cambio ricetta
                  capita quasi sempre ed è un costo di base — scriverlo per esteso
                  su ogni riga copriva i motivi che contano. */}
              <SetupTag tipo={r.setup_tipo} minuti={r.setup_minuti} />
              {r.giorni_ritardo > 0 && (
                <span
                  className={cn(
                    "shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-medium",
                    // ⚠️ Un ritardo su una commessa a chiamata non è un ritardo
                    // verso nessuno: si vede, ma non con lo stesso rosso.
                    r.ritiro_stato === "a_chiamata"
                      ? "border border-dashed text-muted-foreground"
                      : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
                  )}
                  title={
                    r.ritiro_stato === "a_chiamata"
                      ? "Commessa a chiamata: la data è un riferimento, non una promessa"
                      : undefined
                  }
                >
                  +{r.giorni_ritardo} gg
                </span>
              )}
              {r.ritiro_stato && r.ritiro_stato !== "ignota" && (
                <span
                  className={cn(
                    "shrink-0 rounded-full px-1.5 py-0.5 text-[10px]",
                    r.ritiro_stato === "tassativa"
                      ? "bg-brand-100 font-medium text-brand-800 dark:bg-brand-950 dark:text-brand-300"
                      : "text-muted-foreground"
                  )}
                  title={`Tipo di commessa riconosciuto con confidenza «${r.ritiro_confidenza}»`}
                >
                  {r.ritiro_stato}
                  {r.ritiro_confidenza === "probabile" && " ?"}
                </span>
              )}
              {/* Il motivo sta sulla riga, non in un tooltip: §4.3 chiede che sia
                  dichiarato, e un motivo che si vede solo passandoci sopra col
                  mouse non è dichiarato. */}
              <span className="w-full text-[11px] text-muted-foreground">{r.motivazione}</span>
              {r.violazioni.map((v, i) => (
                <span
                  key={`${r.id}-${i}`}
                  className="w-full text-[11px] text-amber-700 dark:text-amber-400"
                >
                  ⚠ {v.regola}: {v.dettaglio}
                </span>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function NessunPiano({
  onGenera,
  inCorso,
  errore,
  onVaiAlFabbisogno,
}: {
  onGenera: () => void;
  inCorso: boolean;
  errore: string | null;
  onVaiAlFabbisogno: () => void;
}) {
  return (
    <div className="rounded-lg border border-dashed bg-muted/40 p-10 text-center">
      <p className="mx-auto max-w-[60ch] text-sm text-muted-foreground">
        Nessuna proposta di piano. Il piano si costruisce dal fabbisogno caricato: cosa produrre,
        in che ordine, su quale macchina.
      </p>
      {errore && (
        <p className="mx-auto mt-3 max-w-[60ch] text-sm text-red-700 dark:text-red-400">{errore}</p>
      )}
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={onGenera}
          disabled={inCorso}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {inCorso ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          Costruisci la proposta
        </button>
        <button
          type="button"
          onClick={onVaiAlFabbisogno}
          className="rounded-md border bg-card px-4 py-2 text-sm font-medium hover:bg-accent/40"
        >
          Vai ai caricamenti
        </button>
      </div>
    </div>
  );
}
